import { useMemo } from 'react';

export interface CartBeat {
  id: string;
  title: string;
  price: number;
  bpm: number;
  key: string;
  genre: string[];
  waveformImage: string;
  audioUrl: string;
  downloadUrl?: string;
  licenseType: string;
  isFree: boolean;
  sold: boolean;
}

export interface CartItemWithDiscount extends CartBeat {
  originalPrice: number;
  discountedPrice: number;
  isBundleFree: boolean;
}

export type BundleTier = 'triple' | 'ultimate' | null;

export interface CartDiscountResult {
  discountedItems: CartItemWithDiscount[];
  subtotal: number;
  totalDiscount: number;
  total: number;
  freeCount: number;
  bundleTier: BundleTier;
  paidItemsCount: number;
  nextTierNeeded: number | null;
}

export function useCartDiscount(cartItems: CartBeat[]): CartDiscountResult {
  return useMemo(() => {
    // Only paid beats (price > 0, not already marked free) count toward bundles
    const paidItems = cartItems.filter(item => item.price > 0 && !item.isFree);

    let freeCount = 0;
    let bundleTier: BundleTier = null;

    if (paidItems.length >= 5) {
      freeCount = 3;
      bundleTier = 'ultimate';
    } else if (paidItems.length >= 3) {
      freeCount = 1;
      bundleTier = 'triple';
    }

    // Sort paid items by price ascending — cheapest get the freebie
    const sortedPaid = [...paidItems].sort((a, b) => a.price - b.price);
    const freeIds = new Set(sortedPaid.slice(0, freeCount).map(i => i.id));

    const discountedItems: CartItemWithDiscount[] = cartItems.map(item => ({
      ...item,
      originalPrice: item.price,
      discountedPrice: freeIds.has(item.id) ? 0 : item.price,
      isBundleFree: freeIds.has(item.id),
    }));

    const subtotal = cartItems.reduce((sum, i) => sum + i.price, 0);
    const totalDiscount = discountedItems.reduce(
      (sum, i) => sum + (i.originalPrice - i.discountedPrice),
      0,
    );
    const total = subtotal - totalDiscount;

    // How many more paid beats until next tier?
    let nextTierNeeded: number | null = null;
    if (paidItems.length < 3) {
      nextTierNeeded = 3 - paidItems.length;
    } else if (paidItems.length < 5) {
      nextTierNeeded = 5 - paidItems.length;
    }

    return {
      discountedItems,
      subtotal,
      totalDiscount,
      total,
      freeCount,
      bundleTier,
      paidItemsCount: paidItems.length,
      nextTierNeeded,
    };
  }, [cartItems]);
}
